import memoizeOne from 'memoize-one'
import React, {useEffect} from 'react'
import {Navigate, NavLink, Routes} from 'react-router-dom'
import {CmdkActionGetter} from '../../cmdk/types'

import {BetaFlagMarker, PreviewFlagMarker} from '../../components/feature-flags'
import {CopyIcon} from '../../components/library/copy'
import {EnvironmentNode} from '../../environment'
import {Flags, useFlags} from '../../hooks/use-flags'
import {useAnalytics} from '../../lib/analytics/analytics'
import {Route} from '../../lib/router'
import {AnalyticsExportSettings} from './analytics-export-settings'
import {MagicLinkSettings} from './magic-link-settings'
import {APIEndpoint} from './api-endpoint'
import {APIHost} from './api-host'
import {APIKeys} from './api-keys'
import {AuthenticationControls} from './authentication-controls'
import {CardActionsConfiguration} from './card-actions'
import {CardApprovals} from './card-approvals'
import {Content, HeaderBox, Main, Menu} from './components'
import {ContainerThemes} from './container-themes'
import {CustomFields} from './custom-fields'
import {Notifications} from './notifications'
import {Salesforce} from './integrations/salesforce'
import {StreamContainers} from './stream-containers'
import {Streams} from './streams'
import {Tags} from './tags'
import {TestAccounts} from './test-accounts'
import {WebhookCredentials} from './webhooks/credentials'
import {CustomEvents} from './webhooks/custom-events'
import {Webhooks} from './webhooks/subscriptions'
import {Marketo} from './integrations/marketo'
import {ShareLinkSettings} from './share-link-settings'

const getLinkGroups = memoizeOne((flags?: Flags) => [
  {
    group: 'SDK',
    links: [
      {to: 'streams', label: 'Streams'},
      {to: 'stream-containers', label: 'Stream containers'},
      {to: 'notifications', label: 'Notifications'},
      {
        to: 'container-themes',
        label: 'Container themes'
      },
      {to: 'api-host', label: 'SDK API host'},
      {to: 'api-keys', label: 'SDK API keys'}
    ]
  },
  {
    group: 'API',
    links: [
      {to: 'api-endpoint', label: 'Base URL endpoint'},
      {to: 'authentication-controls', label: 'API keys'}
    ]
  },
  {
    group: 'Card templates',
    links: [
      {to: 'card-approvals', label: 'Approvals'},
      ...(flags?.featurePreview || flags?.cardActions
        ? [{to: 'card-actions', label: 'Card actions', flag: 'preview'}]
        : []),
      {to: 'share-links', label: 'Share links'}
    ]
  },
  {
    group: 'Customer profiles',
    links: [
      {to: 'custom-fields', label: 'Custom fields', flag: 'beta'},
      {to: 'tags', label: 'Tags'}
    ]
  },
  ...(flags?.featurePreview || flags?.integrations
    ? [
        {
          group: 'Integrations',
          links: [
            {
              to: 'integrations/salesforce',
              label: 'Salesforce',
              flag: 'preview'
            },
            ...(flags?.cardMarketoConnector
              ? [
                  {
                    to: 'integrations/marketo',
                    label: 'Marketo',
                    flag: 'preview'
                  }
                ]
              : [])
          ]
        }
      ]
    : []),
  {
    group: 'Analytics',
    links: [
      {to: 'webhook/subscriptions', label: 'Webhook subscriptions'},
      {to: 'webhook/credentials', label: 'Webhook credentials'},
      {to: 'webhook/custom-events', label: 'Custom events', flag: 'beta'},
      {to: 'analytics-export-settings', label: 'Analytics export settings'}
    ]
  },
  {
    group: 'Testing',
    links: [
      {to: 'test-accounts', label: 'Test accounts'},
      ...(flags?.featurePreview || flags?.authenticatedMagicLinks
        ? [{to: 'magic-link-settings', label: 'Magic links', flag: 'preview'}]
        : [])
    ]
  }
])

export const Configuration = ({
  environment
}: {
  environment: EnvironmentNode
}) => {
  const flags = useFlags(environment.organisationId)

  const analytics = useAnalytics()

  useEffect(() => {
    analytics.track({
      event: 'Viewed configuration home',
      properties: {
        category: 'configuration',
        org_id: environment.organisationId,
        environment_id: environment.id
      }
    })
  }, [analytics, environment])

  return (
    <>
      <HeaderBox>
        <h2>Configuration</h2>
        <ul>
          <li>
            <h6>Organisation</h6>
            <p>{environment.organisationName}</p>
          </li>
          <li>
            <h6>Organization ID</h6>
            <p>
              {environment.organisationId}
              <CopyIcon value={environment.organisationId} />
            </p>
          </li>
          <li>
            <h6>Environment</h6>
            <p>{environment.name}</p>
          </li>
          <li>
            <h6>Environment ID</h6>
            <p>
              {environment.id}
              <CopyIcon value={environment.id} />
            </p>
          </li>
        </ul>
      </HeaderBox>
      <Content>
        <Menu>
          {getLinkGroups(flags).map(group => (
            <React.Fragment key={group.group}>
              <h3>{group.group}</h3>
              <ul>
                {group.links.map(link => (
                  <li key={link.to}>
                    <NavLink to={link.to}>
                      {link.label}{' '}
                      {link.flag === 'preview' && <PreviewFlagMarker />}
                      {link.flag === 'beta' && <BetaFlagMarker />}
                    </NavLink>
                  </li>
                ))}
              </ul>
            </React.Fragment>
          ))}
        </Menu>
        <Main>
          <Routes>
            <Route path="/" render={() => <Navigate to="streams" replace />} />
            <Route
              path="/streams"
              render={() => <Streams environment={environment} />}
            />
            <Route
              path="/stream-containers"
              render={() => <StreamContainers environment={environment} />}
            />
            <Route
              path="/notifications"
              render={() => <Notifications environment={environment} />}
            />
            <Route
              path="/container-themes"
              render={() => <ContainerThemes environment={environment} />}
            />
            <Route
              path="/api-host"
              render={() => <APIHost environment={environment} />}
            />
            <Route
              path="/api-keys"
              render={() => <APIKeys environment={environment} />}
            />
            <Route
              path="/api-endpoint"
              render={() => <APIEndpoint environment={environment} />}
            />
            <Route
              path="/authentication-controls"
              render={() => (
                <AuthenticationControls environment={environment} />
              )}
            />
            <Route
              path="/tags"
              render={() => <Tags environment={environment} />}
            />
            <Route
              path="/custom-fields"
              render={() => <CustomFields environment={environment} />}
            />
            <Route
              path="/webhook/subscriptions"
              render={() => <Webhooks environment={environment} />}
            />
            <Route
              path="/webhook/custom-events"
              render={() => <CustomEvents environment={environment} />}
            />
            <Route
              path="/webhook/credentials"
              render={() => <WebhookCredentials environment={environment} />}
            />
            <Route
              path="/analytics-export-settings"
              render={() => (
                <AnalyticsExportSettings environment={environment} />
              )}
            />
            <Route
              path="/test-accounts"
              render={() => <TestAccounts environment={environment} />}
            />
            <Route
              path="/magic-link-settings"
              render={() => <MagicLinkSettings environment={environment} />}
            />
            <Route
              path="/card-approvals"
              render={() => <CardApprovals environment={environment} />}
            />
            <Route
              path="/share-links"
              render={() => <ShareLinkSettings environment={environment} />}
            />
            <Route
              path="/card-actions"
              render={() => (
                <CardActionsConfiguration environment={environment} />
              )}
            />
            <Route
              path="/integrations/salesforce"
              render={() => <Salesforce environment={environment} />}
            />
            <Route
              path="/integrations/marketo"
              render={() => <Marketo environment={environment} />}
            />
          </Routes>
        </Main>
      </Content>
    </>
  )
}

export const getActionsForConfiguration: CmdkActionGetter = flags =>
  getLinkGroups(flags).flatMap(group =>
    group.links.map(link => ({
      name: link.label,
      to: `configuration/${link.to}`,
      parent: 'configuration'
    }))
  )
